import type * as React from 'react';
import { twMerge } from '@/lib/twMerge';
export interface PremiumStarProps extends React.SVGAttributes<SVGSVGElement> {
  className?: string;
  hideBorder?: boolean;
  pulseInfinite?: boolean;
  showBackground?: boolean;
}
const PremiumStar = (props: PremiumStarProps) => {
  const {
    showBackground = true,
    className,
    hideBorder,
    pulseInfinite = false,
    ...rest
  } = props;
  return <div className={twMerge('relative flex aspect-square items-center justify-center rounded-full', className)} data-sentry-component="PremiumStar" data-sentry-source-file="PremiumStar.tsx">
      {!!pulseInfinite && <>
          <div className="circle circle2" />
          <div className="circle circle3" />
          <style>{`
            .circle {
              position: absolute;
              inset: 0;
              border-radius: 9999px;
              background-color: var(--uppbeat);
              opacity: 0;
              transform: scale(1);
              pointer-events: none;
              z-index: 10;
            }

            .circle2 {
              animation: circle2-pulse 1.8s infinite ease-in-out;
            }

            .circle3 {
              animation: circle3-pulse 1.8s infinite ease-in-out;
            }

            @keyframes circle2-pulse {
              /* Frame 1: Only premium star visible */
              0%,
              15% {
                opacity: 1;
                transform: scale(1);
              }

              /* Frame 2: Circle 2 appears at full opacity */
              20% {
                opacity: 1;
                transform: scale(1);
              }
              /* As it expands to 50%, opacity gradually reduces */
              40% {
                opacity: 0.2;
                transform: scale(1.5);
              }

              /* Minimal delay */
              41% {
                opacity: 0.2;
                transform: scale(1.5);
              }

              /* Frame 3: Circle 2 reaches 100% */
              60% {
                opacity: 0.15;
                transform: scale(2);
              }

              /* Circle 2 fades out gradually */
              70% {
                opacity: 0.05;
                transform: scale(2);
              }
              75%,
              100% {
                opacity: 0;
                transform: scale(2);
              }
            }

            @keyframes circle3-pulse {
              /* Frames 1 & 2: Hidden */
              0%,
              40% {
                opacity: 1;
                transform: scale(1);
              }

              /* Minimal delay */
              41% {
                opacity: 1;
                transform: scale(1);
              }

              /* Circle 3 appears at full opacity */
              42% {
                opacity: 1;
                transform: scale(1);
              }
              /* As it expands to 50%, opacity gradually reduces */
              50% {
                opacity: 0.2;
                transform: scale(1.25);
              }
              /* Reach 50% expansion with reduced opacity */
              60% {
                opacity: 0.2;
                transform: scale(1.5);
              }

              /* Continue expansion after circle 2 is done */
              75% {
                opacity: 0.15;
                transform: scale(2);
              }
              /* Shorter hold at full size */
              80% {
                opacity: 0.15;
                transform: scale(2);
              }
              /* Faster fade out */
              85% {
                opacity: 0.05;
                transform: scale(2);
              }
              90%,
              100% {
                opacity: 0;
                transform: scale(2);
              }
            }
          `}</style>
        </>}
      <svg className="z-10 size-full fill-pink-500" fill="none" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg" {...rest} aria-hidden="true" data-sentry-element="svg" data-sentry-source-file="PremiumStar.tsx">
        <path d="M10 0.75C15.1086 0.75 19.25 4.89137 19.25 10C19.25 15.1086 15.1086 19.25 10 19.25C4.89137 19.25 0.75 15.1086 0.75 10C0.75 4.89137 4.89137 0.75 10 0.75Z" fill="#F23D75" data-sentry-element="path" data-sentry-source-file="PremiumStar.tsx" />
        {!hideBorder && <path className="stroke-white dark:stroke-gray-950" d="M10 0.75C15.1086 0.75 19.25 4.89137 19.25 10C19.25 15.1086 15.1086 19.25 10 19.25C4.89137 19.25 0.75 15.1086 0.75 10C0.75 4.89137 4.89137 0.75 10 0.75Z" strokeWidth="1.5" />}

        <g clipPath="url(#clip0_2353_12663)" data-sentry-element="g" data-sentry-source-file="PremiumStar.tsx">
          <path d="M13.7538 8.69994L11.3842 8.45702L10.4221 6.27828C10.2613 5.91453 9.745 5.91453 9.58417 6.27828L8.62208 8.45702L6.25208 8.70036C5.85667 8.74077 5.69708 9.23161 5.99333 9.49703L7.76792 11.0866L7.26583 13.4141C7.18208 13.8029 7.6 14.1066 7.94375 13.9066L10.0033 12.7079L12.0625 13.9062C12.4063 14.1062 12.8246 13.8029 12.7404 13.4137L12.2379 11.0866L14.0129 9.49703C14.3092 9.23161 14.1496 8.74077 13.7542 8.70036L13.7538 8.69994Z" fill="white" stroke="white" strokeMiterlimit="10" data-sentry-element="path" data-sentry-source-file="PremiumStar.tsx" />
        </g>
        <defs data-sentry-element="defs" data-sentry-source-file="PremiumStar.tsx">
          <clipPath id="clip0_2353_12663" data-sentry-element="clipPath" data-sentry-source-file="PremiumStar.tsx">
            <rect fill="white" height="10" transform="translate(5 5)" width="10" data-sentry-element="rect" data-sentry-source-file="PremiumStar.tsx" />
          </clipPath>
        </defs>
      </svg>
    </div>;
};

// Export without memo to ensure animation always starts fresh
export default PremiumStar;